import {
  DialogComponent,
  DialogOpenEvent,
  DialogCloseEvent,
} from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { isMobileBreakpoint } from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { createInlineMessageElement } from "./benvel-state-system.js";

/**
 * A custom element that manages a cart drawer.
 *
 * Story 5.1 — SRA partial swap + Benvel event bus + reduced-motion.
 *
 * On a successful cart-add (Horizon's `CartAddEvent` → `cart:update`), the
 * drawer refreshes itself via the Section Rendering API endpoint
 * `/cart?section_id=cart-drawer` BEFORE opening, so the new line item is
 * already in the DOM the moment the dialog appears (no full-page reload,
 * no flash-of-empty content).
 *
 * Benvel events (documented in `docs/events.md`):
 *   - `benvel:cart-opened`  — fires after `showDialog()` completes
 *   - `benvel:cart-closed`  — fires after `closeDialog()` completes
 *   - `benvel:cart-updated` — fires after the SRA swap morphs new totals in
 *
 * `prefers-reduced-motion: reduce` is honored via CSS (paired stylesheet
 * `assets/benvel-cart-drawer.css`); no JS class toggling needed.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * The reason the dialog is opening / closing. Set by callers before the
   * dialog state transition; consumed by `#dispatchBenvelOpen` /
   * `#dispatchBenvelClose`. Defaults capture the common cases.
   * @type {'icon-click'|'add-to-cart'|'sra-refresh'|'programmatic'}
   */
  #openSource = "icon-click";

  /** @type {'esc'|'close-button'|'popstate'|'programmatic'} */
  #closeSource = "close-button";

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#dispatchBenvelOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#dispatchBenvelClose,
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState,
    );
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen,
    );
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#dispatchBenvelOpen,
    );
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose,
    );
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#dispatchBenvelClose,
    );
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, "");
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener("popstate", this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.#closeSource = "popstate";
      this.refs.dialog.style.setProperty(
        "--dialog-drawer-closing-animation",
        "none",
      );
      await this.closeDialog();
      this.refs.dialog.style.removeProperty(
        "--dialog-drawer-closing-animation",
      );
    }
  };

  /**
   * Handles cart add events — refreshes drawer contents via SRA (so the new
   * line item is in the DOM at open time) and announces count.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = async (event) => {
    this.#openSource = "add-to-cart";

    // Refresh drawer contents via Section Rendering API BEFORE opening so
    // the new line item is already morphed in (AC1 — no flash-of-empty).
    // Failure is non-fatal: the drawer still opens with whatever was there.
    let cart = null;
    try {
      const [, cartJson] = await Promise.all([
        sectionRenderer.renderSection("cart-drawer", {
          url: new URL("/cart", window.location.origin),
          cache: false,
        }),
        this.#fetchCartJson(),
      ]);
      cart = cartJson;
    } catch (error) {
      // Log a warning but proceed to open the drawer — graceful degradation.
      // Common cause: network blip or section-rendering 404 during dev.
      // eslint-disable-next-line no-console
      console.warn(
        "[cart-drawer] SRA refresh failed; opening with stale content.",
        error,
      );
      // AC6 (Story 19.4) — surface inline-message on network failure.
      // Locale key: state.inline.network_error (es-CO tuteo + en mirror).
      // Falls back to hardcoded string if Theme.translations is unavailable
      // (non-drawer contexts, tests, or scripts.liquid not yet updated).
      const networkErrorMsg =
        (typeof Theme !== "undefined" &&
          Theme.translations?.state_inline_network_error) ||
        "Algo falló al actualizar tu bolsa. Intenta de nuevo.";
      const inlineMsgNetwork = createInlineMessageElement({
        tone: "warning",
        message: networkErrorMsg,
        dense: true,
      });
      if (inlineMsgNetwork) {
        // Insert at the top of the drawer content area (inside .cart-drawer__content).
        // The #benvel-cart-network-error id allows idempotent replacement on
        // repeated failures (e.g. rapid re-opens while still offline).
        inlineMsgNetwork.id = "benvel-cart-network-error";
        const dialog = this.refs.dialog;
        const existingError = dialog?.querySelector("#benvel-cart-network-error");
        if (existingError) {
          existingError.replaceWith(inlineMsgNetwork);
        } else {
          const content = dialog?.querySelector(".cart-drawer__content");
          content?.prepend(inlineMsgNetwork);
        }
        // Auto-clear the network-error message after ERROR_DISPLAY_DURATION_MS
        // so it does not persist across a successful subsequent add.
        const ERROR_DISPLAY_DURATION_MS = 10000;
        setTimeout(() => {
          dialog?.querySelector("#benvel-cart-network-error")?.remove();
        }, ERROR_DISPLAY_DURATION_MS);
      }
    }

    if (this.hasAttribute("auto-open")) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // AC8 — fire `benvel:cart-updated` AFTER the SRA swap morphs new totals
    // in (downstream of the product-form's own fire; consumers dedupe by
    // `detail.source`; see docs/events.md "benvel:cart-updated").
    this.#dispatchCartUpdated(cart, "add-to-cart");
  };

  /**
   * Dispatches `benvel:cart-opened` on document after the dialog has opened.
   * Fetches `/cart.js` in parallel to populate the totals payload; tolerates
   * fetch failure by emitting null totals.
   */
  #dispatchBenvelOpen = async () => {
    const source = this.#openSource;
    this.#openSource = "icon-click"; // reset for next open
    const cart = await this.#fetchCartJson();
    document.dispatchEvent(
      new CustomEvent("benvel:cart-opened", {
        bubbles: true,
        detail: {
          source,
          cartItemCount: cart?.item_count ?? null,
          cartTotal: cart?.total_price ?? null,
        },
      }),
    );
  };

  /**
   * Dispatches `benvel:cart-closed` on document after the dialog has closed.
   */
  #dispatchBenvelClose = async () => {
    const source = this.#closeSource;
    this.#closeSource = "close-button"; // reset for next close
    const cart = await this.#fetchCartJson();
    document.dispatchEvent(
      new CustomEvent("benvel:cart-closed", {
        bubbles: true,
        detail: {
          source,
          cartItemCount: cart?.item_count ?? null,
        },
      }),
    );
  };

  /**
   * Dispatches `benvel:cart-updated` on document with current cart totals.
   * Dual-fire decision (Option 1 per Story 5.1 AC8): the drawer fires AFTER
   * its SRA swap completes, downstream of the product-form's own fire.
   * Consumers MUST dedupe by `detail.source`; see docs/events.md.
   *
   * @param {object | null} cart - The `/cart.js` JSON shape, or null if the fetch failed.
   * @param {'add-to-cart'|'line-edit'|'remove'|'sra-refresh'} source
   */
  #dispatchCartUpdated(cart, source) {
    document.dispatchEvent(
      new CustomEvent("benvel:cart-updated", {
        bubbles: true,
        detail: {
          cart,
          source,
          cartItemCount: cart?.item_count ?? null,
          cartTotal: cart?.total_price ?? null,
        },
      }),
    );
  }

  /**
   * Fetches the current cart as JSON. Returns `null` on failure (network
   * blip, 4xx, parse error); callers MUST treat null totals as "unknown".
   *
   * @returns {Promise<object | null>}
   */
  async #fetchCartJson() {
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (
      this.refs.liveRegion
    );
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined)
      return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#openSource = "icon-click";
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.#closeSource = "programmatic";
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      "cart-summary-sticky",
      ratio > this.#summaryThreshold ? "false" : "true",
    );
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
